import { useState, useCallback } from 'react';

/**
 * Lightweight form state hook — avoids the react-hook-form dependency
 * for simple CRUD forms. Returns bind helpers for onChange/value.
 */
export function useFormFields<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);

  const bind = useCallback(
    (name: keyof T) => ({
      value: values[name] as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
        setValues((v) => ({ ...v, [name]: e.target.value })),
    }),
    [values],
  );

  const setField = useCallback((name: keyof T, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
  }, []);

  const patchValues = useCallback((partial: Partial<T>) => {
    setValues((v) => ({ ...v, ...partial }));
  }, []);

  const reset = useCallback((next: T) => setValues(next), []);

  return { values, bind, setField, patchValues, reset };
}

